import { LatestIdentification } from '../../dna/DnaIdentificationsPanel';
import { canDo } from '../stages';
import { Day, Ltr, Taxon, useDnaT } from '../cells';
import * as S from '../../specimens/styles';
import { intakeNav } from '../../../utils/linkedRecordNav';
import { SpeciesBioProfile } from '../../bioprofile/BioProfile';
import RarityGauge from '../../rarity/RarityGauge';

/*
 * Taxonomy: the bank's dictionary entry for what the isolate is called
 * (general_data), with a link to where it sits in the taxonomy tree
 * (/bank/taxonomy?node=, the entry's tree_node: the server applies the tree's
 * rules), then every identification made of it -- the collection's own
 * history and the DNA answers.
 */
const FIELDS = ['taxonomic_rank', 'phylum', 'class_name', 'order_name', 'family', 'genus', 'trophic_mode',
  'growth_form', 'culture_protocol', 'culture_temperature'];

const plain = (value) => String(value).replace(/_/g, ' ');

/** A dictionary value in the reader's words where it is one of a fixed set:
 *  the rank as the identification history says it (common:rank), the
 *  trophic mode by the taxonomy catalogue's labels (FUNGuild's composite values,
 *  which have no label, as written). Names and free text stay as written; a
 *  temperature ("20-25 °C") keeps its unit on the right, right to left too. */
function taxonValue(field, value, t) {
  if (field === 'taxonomic_rank') return t(`common:rank.${value}`, { defaultValue: plain(value) });
  if (field === 'trophic_mode') return t(`taxonomy:trophicMode.${value}`, { defaultValue: plain(value) });
  if (field === 'culture_temperature') return <Ltr>{plain(value)}</Ltr>;
  return plain(value);
}

export default function TaxonomyTab({ item, dossier, user, t, onAction, onNavigate }) {
  const tDna = useDnaT();
  const taxon = dossier.taxonomy;
  const history = [...(dossier.identifications?.history || [])]
    .sort((a, b) => String(b.identification_date || b.at || '').localeCompare(String(a.identification_date || a.at || '')));
  const dnaAnswers = dossier.identifications?.dna || [];

  return (
    <div>
      {canDo(user, 'identify', item) && (
        <div className="hub-tab-actions">
          <button type="button" style={S.ghostBtn} onClick={() => onAction('identify', item)}
            title={t('tip.action.identify')}>{t('action.identify')}</button>
        </div>
      )}
      {!canDo(user, 'identify', item) && item.external_id && !item.sample_id && (
        <p role="note" style={S.hint}>
          {t('taxonomy.intakeNote')}{' '}
          <button type="button" className="hub-link" title={t('tip.openIntake')}
            onClick={() => onNavigate(intakeNav(item.external_id).path)}>{t('taxonomy.openIntake')}</button>
        </p>
      )}

      <h4 style={S.sectionTitle}>{t('taxonomy.entry')}</h4>
      {!taxon && <p style={S.hint}>{t('taxonomy.noEntry')}</p>}
      {taxon && (
        <div data-testid="hub-taxonomy">
          <p style={{ margin: '0 0 8px' }}>
            <Taxon name={taxon.scientific_name || taxon.matched_name} />
            {taxon.hebrew_name && <span style={{ marginInlineStart: 8, color: 'var(--c-text-muted-fg)' }}>{taxon.hebrew_name}</span>}
          </p>
          {taxon.matched_name && taxon.scientific_name && taxon.matched_name !== taxon.scientific_name && (
            <p style={S.hint}>{t('taxonomy.matched', { name: taxon.matched_name })}</p>
          )}
          {taxon.tree_node && (
            <p style={{ margin: '0 0 8px' }}>
              <button type="button" className="hub-link" data-testid="hub-taxonomy-tree"
                onClick={() => onNavigate(`/bank/taxonomy?${new URLSearchParams(
                  { node: taxon.tree_node })}`)}>
                {t('taxonomy.inTree')}
              </button>
            </p>
          )}
          <dl style={S.dl}>
            {FIELDS.filter((f) => taxon[f]).map((f) => (
              <div key={f} style={{ display: 'contents' }}>
                <dt style={S.dt}>{t(`taxonomy.fields.${f}`)}</dt>
                <dd style={S.dd} data-testid={`hub-taxon-${f}`}>{taxonValue(f, taxon[f], t)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {/* How rare its taxon is, for specimen:read (routes/rarity_index.py). */}
      <RarityGauge specimenId={item.specimen_id} user={user} />
      {/* The species' (or its family's) scientific profile: an isolate shows its
          species', whole; nothing here is the isolate's own. */}
      <SpeciesBioProfile name={taxon?.scientific_name || item.taxon_name} tier="full" user={user}
                         testId="hub-bioprofile" />

      <h4 style={{ ...S.sectionTitle, marginTop: 16 }}>{t('taxonomy.history')}</h4>
      {history.length === 0 && dnaAnswers.length === 0 && <p style={S.hint}>{t('taxonomy.noHistory')}</p>}
      {history.length > 0 && (
        <div className="table-wrap">
          <table style={{ borderCollapse: 'collapse', fontSize: 13 }} data-testid="hub-ident-history">
            <thead>
              <tr>
                {['date', 'taxon', 'rank', 'confidence', 'method', 'approved'].map((c) => (
                  <th key={c} scope="col" style={th}>{t(`taxonomy.columns.${c}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={h.identification_id || i}>
                  <td style={tdc}><Day value={h.identification_date || h.at} /></td>
                  <td style={tdc}><Taxon name={h.taxon_name} /></td>
                  <td style={tdc}>{h.taxonomic_rank ? t(`common:rank.${h.taxonomic_rank}`) : '—'}</td>
                  <td style={tdc}>{h.confidence ? t(`common:confidence.${h.confidence}`) : '—'}</td>
                  <td style={tdc}>{h.identification_method ? t(`ident.methods.${h.identification_method}`, { defaultValue: h.identification_method }) : '—'}</td>
                  <td style={tdc}>{h.expert_approval ? `✓ ${t('ident.approved')}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dnaAnswers.length > 0 && (
        <ul className="hub-records" style={{ marginTop: 10 }}>
          {dnaAnswers.map((d, i) => (
            <li key={d.identification_id || i} className="hub-record">
              <span style={S.hint}>{t('ident.dna')} {d.marker || d.gene_name || ''} · <Day value={d.analyzed_at} /></span>
              <div><LatestIdentification latest={d} t={tDna} /></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const th = { padding: '4px 8px', textAlign: 'start', fontWeight: 600, whiteSpace: 'nowrap' };
const tdc = { padding: '4px 8px', borderTop: '1px solid var(--c-border)' };
